import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Stack,
  Divider,
  Alert,
  useTheme,
} from '@mui/material'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import { useAccount } from '../hooks/useAccount'
import { initiateGoogleLogin } from '../api/auth'

// Google's official "G" SVG logo
function GoogleLogo() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48">
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.2l6.7-6.7C35.7 2.4 30.2 0 24 0 14.7 0 6.7 5.4 2.7 13.3l7.8 6.1C12.4 13.1 17.7 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v8.5h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4 6.9-10 6.9-17z"/>
      <path fill="#FBBC05" d="M10.5 28.6A14.7 14.7 0 0 1 9.5 24c0-1.6.3-3.2.8-4.6l-7.8-6.1A23.9 23.9 0 0 0 0 24c0 3.9.9 7.5 2.6 10.7l7.9-6.1z"/>
      <path fill="#34A853" d="M24 48c6.2 0 11.4-2 15.2-5.5l-7.5-5.8c-2 1.4-4.6 2.2-7.7 2.2-6.3 0-11.6-3.6-13.5-9.2l-7.9 6.1C6.7 42.6 14.7 48 24 48z"/>
    </svg>
  )
}

const FEATURE_ICONS = ['📧', '📞', '📅', '📊'] as const
const FEATURE_KEYS = ['gmail', 'phone', 'calendar', 'reports'] as const

export default function Login() {
  const { data: user, isLoading } = useAccount()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'

  const errorCode = searchParams.get('error')
  const errorMessage = errorCode
    ? t(`login.errors.${errorCode}`, { defaultValue: t('login.errors.unknown') })
    : null

  useEffect(() => {
    if (!isLoading && user) {
      navigate('/dashboard', { replace: true })
    }
  }, [user, isLoading, navigate])

  const borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: isDark
          ? 'linear-gradient(135deg, #0f1117 0%, #1a1f35 100%)'
          : 'linear-gradient(135deg, #e8eaf6 0%, #f5f6fa 100%)',
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 420, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Stack alignItems="center" spacing={3}>
            {/* Brand */}
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  p: 2,
                  borderRadius: '50%',
                  background: 'rgba(66,133,244,0.15)',
                  mb: 2,
                }}
              >
                <NotificationsActiveIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              </Box>
              <Typography variant="h5" fontWeight={700}>
                {t('login.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                {t('login.subtitle')}
              </Typography>
            </Box>

            <Divider sx={{ width: '100%', borderColor }} />

            {/* Feature highlights */}
            <Stack spacing={1.5} sx={{ width: '100%' }}>
              {FEATURE_KEYS.map((key, i) => (
                <Typography key={key} variant="body2" color="text.secondary">
                  {FEATURE_ICONS[i]}  {t(`login.features.${key}`)}
                </Typography>
              ))}
            </Stack>

            <Divider sx={{ width: '100%', borderColor }} />

            {/* Error banner */}
            {errorMessage && (
              <Alert severity="error" sx={{ width: '100%' }}>
                {errorMessage}
              </Alert>
            )}

            {/* Sign in button */}
            <Stack spacing={2} sx={{ width: '100%' }}>
              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={<GoogleLogo />}
                onClick={initiateGoogleLogin}
                sx={{
                  background: '#4285F4',
                  color: '#fff',
                  py: 1.5,
                  fontSize: '1rem',
                  '&:hover': { background: '#1A73E8' },
                }}
              >
                {t('login.signIn')}
              </Button>
            </Stack>

            <Typography variant="caption" color="text.secondary" textAlign="center" sx={{ whiteSpace: 'pre-line' }}>
              {t('login.hint')}
            </Typography>

          </Stack>
        </CardContent>
      </Card>
    </Box>
  )
}
